import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import { useBookingForm } from '../hooks/useBookingForm';
import type { BookingFields, BookingRequest } from '../types/booking';
import type { Lang, ScheduleOption } from '../types/copy';
interface BookingFormProps {
  request: BookingRequest | null;
}
const SCHEDULES: ScheduleOption[] = ['morning', 'afternoon', 'evening', 'flexible'];
const LANGS: Lang[] = ['en', 'es'];
const inputBase = 'w-full rounded-xl border bg-white/[0.04] px-4 text-[16px] text-cream placeholder:text-cream/40 outline-none transition-[border-color,box-shadow] duration-200 focus:border-violet-light focus:shadow-[0_0_0_4px_rgba(196,181,253,0.15)]';
export function BookingForm({
  request
}: BookingFormProps) {
  const {
    t,
    lang
  } = useLanguage();
  const f = t.contact.form;
  const {
    fields,
    errors,
    status,
    update,
    submit,
    reset
  } = useBookingForm(request, lang);
  const sendToWhatsApp = (data: BookingFields) => {
    const serviceLabel = data.service === 'other' ? f.other : t.services.items.find(s => s.id === data.service)?.title ?? t.memberships.tiers.find(m => m.id === data.service)?.name ?? '';
    const details = [`${f.name}: ${data.name}`, `${f.email}: ${data.email}`, `${f.phone}: ${data.phone}`, `${f.service}: ${serviceLabel}`, `${f.schedule}: ${f.scheduleOptions[data.schedule]}`, `${f.language}: ${f.languageOptions[data.language]}`].join('\n');
    const note = data.message.trim();
    const text = [f.whatsappIntro, details, note].filter(Boolean).join('\n\n');
    const url = `${business.whatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const borderFor = (invalid?: boolean) => invalid ? 'border-[#FCA5A5]' : 'border-white/15';
  return <div className="rounded-[28px] bg-ink p-7 md:p-10" aria-live="polite" data-id="element-5" data-mp="3q9b2bpv95hjl">
      <AnimatePresence mode="wait" initial={false} data-id="element-6" data-mp="3v7uljiigof89">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex min-h-[420px] flex-col items-start justify-center" data-id="element-7" data-mp="3giq0zuo71ywz">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage text-ink" data-id="element-8" data-mp="3bk50du515d80">
              <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-9" data-mp="2xl5daqx616dy" />
            </span>
            <h3 className="mt-6 font-display text-[26px] font-medium text-white" data-id="element-10" data-mp="32jqcqo4lfm1c">{f.successTitle}</h3>
            <p className="mt-3 max-w-md text-[16.5px] text-cream/75" data-id="element-11" data-mp="vt907cvcnoh2">{f.successBody}</p>
            <button type="button" onClick={reset} className="btn btn-ghost-light mt-8" data-id="element-12" data-mp="10rkebzsogl53">
              {f.another}
            </button>
          </motion.div> : <motion.form key="form" noValidate onSubmit={e => submit(e, sendToWhatsApp)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="grid gap-6" data-id="element-13" data-mp="2ga9th3k5bcn7">
            <Field id="booking-name" label={f.name} error={errors.name ? f.errors.name : undefined} data-id="element-14" data-mp="33z1glgivp4t4">
              <input id="booking-name" type="text" autoComplete="name" required value={fields.name} onChange={e => update('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'booking-name-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.name)}`} data-id="element-15" data-mp="2z1j5bjhe2dhy" />
            </Field>

            <div className="grid gap-6 md:grid-cols-2" data-id="element-16" data-mp="5ohe47mx6rme">
              <Field id="booking-email" label={f.email} error={errors.email ? f.errors.email : undefined} data-id="element-17" data-mp="5npqpk3h8077">
                <input id="booking-email" type="email" autoComplete="email" required value={fields.email} onChange={e => update('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'booking-email-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.email)}`} data-id="element-18" data-mp="ap5ehjx1gkta" />
              </Field>
              <Field id="booking-phone" label={f.phone} error={errors.phone ? f.errors.phone : undefined} data-id="element-19" data-mp="ov03vz9p7cj">
                <input id="booking-phone" type="tel" autoComplete="tel" required value={fields.phone} onChange={e => update('phone', e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'booking-phone-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.phone)}`} data-id="element-20" data-mp="30cmjomiqnko2" />
              </Field>
            </div>

            <Field id="booking-service" label={f.service} error={errors.service ? f.errors.service : undefined} data-id="element-21" data-mp="2itmemo9xr0xc">
              <div className="relative" data-id="element-22" data-mp="2954jamkfdm13">
                <select id="booking-service" required value={fields.service} onChange={e => update('service', e.target.value as BookingFields['service'])} aria-invalid={!!errors.service} aria-describedby={errors.service ? 'booking-service-error' : undefined} className={`${inputBase} h-14 appearance-none pr-12 ${borderFor(errors.service)} ${fields.service ? '' : 'text-cream/50'}`} data-id="element-23" data-mp="2942vdhib951y">
                  <option value="" disabled className="text-ink" data-id="element-24" data-mp="1li0nkqbo89cf">
                    {f.servicePlaceholder}
                  </option>
                  <optgroup label={t.services.title} className="text-ink" data-id="element-25" data-mp="16lrphs3io21l">
                    {t.services.items.map(s => <option key={s.id} value={s.id} className="text-ink" data-id="element-26" data-mp="1bl4nn8zu8vjh">
                        {s.title}
                      </option>)}
                  </optgroup>
                  <optgroup label={t.memberships.title} className="text-ink" data-id="element-27" data-mp="24a1rw877srt8">
                    {t.memberships.tiers.map(m => <option key={m.id} value={m.id} className="text-ink" data-id="element-28" data-mp="1uhvzwygyqae1">
                        {m.name} — {m.term}
                      </option>)}
                  </optgroup>
                  <option value="other" className="text-ink" data-id="element-29" data-mp="2mz01mef0ay94">
                    {f.other}
                  </option>
                </select>
                <ChevronDownIcon aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/60" data-id="element-30" data-mp="bhwuug1nt29m" />
              </div>
            </Field>

            <div className="grid gap-6 md:grid-cols-[1fr_auto]" data-id="element-31" data-mp="ga7r98x6y5xf">
              <PillGroup legend={f.schedule} name="schedule" options={SCHEDULES.map(s => ({
            value: s,
            label: f.scheduleOptions[s]
          }))} value={fields.schedule} onChange={v => update('schedule', v as ScheduleOption)} data-id="element-32" data-mp="gazoeo6fsbpt" />
              <PillGroup legend={f.language} name="language" options={LANGS.map(l => ({
            value: l,
            label: f.languageOptions[l]
          }))} value={fields.language} onChange={v => update('language', v as Lang)} data-id="element-33" data-mp="1iyw6kairscw" />
            </div>

            <div data-id="element-34" data-mp="32huh4cv5rnzl">
              <label htmlFor="booking-message" className="flex items-baseline gap-2 text-[14px] font-medium text-cream" data-id="element-35" data-mp="32idnqxfcypyb">
                {f.message}
                <span className="text-[13px] font-normal text-cream/50" data-id="element-36" data-mp="37ekvmvrbebiw">{f.optional}</span>
              </label>
              <textarea id="booking-message" rows={4} value={fields.message} onChange={e => update('message', e.target.value)} placeholder={f.messagePlaceholder} className={`${inputBase} mt-2 resize-none border-white/15 py-3.5 leading-[1.6]`} data-id="element-37" data-mp="3cdxg59upr6ib" />
            </div>

            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-38" data-mp="3v71eh8ivm7j9">
              <p className="text-[13px] text-cream/55" data-id="element-39" data-mp="3z71fxz8thvy">{f.privacyNote}</p>
              <button type="submit" className="btn bg-[#0F7B6C] text-white hover:bg-[#0B5E53]" data-id="element-40" data-mp="20ta5mb8dfrov">
                <WhatsAppGlyph data-id="element-41" data-mp="25t6bkg0mpt72" />
                {f.submit}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
function WhatsAppGlyph() {
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" data-id="element-42" data-mp="e3wybpfshvtb">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 5.83 2.42 8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.12-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48a.92.92 0 0 0-.66.31c-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29Z" data-id="element-43" data-mp="e1j2hedacfkp" />
    </svg>;
}
function Field({
  id,
  label,
  error,
  children
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-44" data-mp="2elaewhpkud3v">
      <label htmlFor={id} className="text-[14px] font-medium text-cream" data-id="element-45" data-mp="2eljynnk13o14">
        {label}
      </label>
      <div className="mt-2" data-id="element-46" data-mp="2sc5ipfl7y40l">{children}</div>
      {error && <p id={`${id}-error`} className="mt-2 text-[13px] text-[#FCA5A5]" data-id="element-47" data-mp="2nf5e7940p9bl">
          {error}
        </p>}
    </div>;
}
function PillGroup({
  legend,
  name,
  options,
  value,
  onChange
}: {
  legend: string;
  name: string;
  options: {
    value: string;
    label: string;
  }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return <fieldset data-id="element-48" data-mp="17zzj1gjycz1x">
      <legend className="text-[14px] font-medium text-cream" data-id="element-49" data-mp="12ys9pd25vrt5">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2" data-id="element-50" data-mp="130v9got48zb2">
        {options.map(o => <label key={o.value} className="cursor-pointer" data-id="element-51" data-mp="3emnn5xgecga6">
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} className="peer sr-only" data-id="element-52" data-mp="3jlqgwlqxf02u" />
            <span className="inline-flex h-11 items-center whitespace-nowrap rounded-full border border-white/15 px-4 text-[14px] text-cream/80 transition-colors duration-150 hover:border-white/40 peer-checked:border-cream peer-checked:bg-cream peer-checked:text-ink peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-violet-light" data-id="element-53" data-mp="39tkt7o7pj4tg">
              {o.label}
            </span>
          </label>)}
      </div>
    </fieldset>;
}